import React, { createContext, useState, useEffect, useContext } from 'react'
import { getStoryblokApi } from '@storyblok/react'

import { getAllStoryblokRequests } from './'
import { useStoryblokBridge } from '../../utils/storyblok'
import config from '../../config'

const { token } = config.storyblok

const StoryblokContext = createContext()
const useStoryblokContext = () => useContext(StoryblokContext)

const StoryblokConsumer = StoryblokContext.Consumer

const StoryblokProvider = ({
  children,
  slug,
  locale,
  apiRequests,
  fallbackData,
}) => {
  const [story, setStory] = useState({})
  const [cvInfo, setCvInfo] = useState(null)

  useEffect(() => {
    getStoryblokCv()
  }, [])

  useEffect(() => {
    if (cvInfo) {
      getStoryblokData()
    }
  }, [cvInfo])

  const getStoryblokCv = async () => {
    const sbApi = getStoryblokApi()

    const cv = window.localStorage.hasOwnProperty('storyblok-cv')
      ? window.localStorage.getItem('storyblok-cv')
      : undefined

    try {
      const response = await sbApi.get(`cdn/spaces/me`, {
        token,
        cv,
      })

      const { version } = response.data?.space
      window.localStorage.setItem('storyblok-cv', version)
      setCvInfo(version)
    } catch (error) {
      console.error(error)
    }
  }

  const sbSlug = slug === '' ? 'home' : slug

  const defaultLocale = locale || 'en'
  const sbLocale = defaultLocale === 'en' ? null : defaultLocale

  useEffect(() => {
    const useBridge = useStoryblokBridge()

    if (useBridge) {
      const bridgeOptions = apiRequests[`${sbSlug}`]
        ? apiRequests[`${sbSlug}`].bridgeOptions || {}
        : {}

      const { StoryblokBridge, location } = window

      if (StoryblokBridge) {
        const storyblokInstance = new StoryblokBridge(bridgeOptions)

        storyblokInstance.on(['input', 'published', 'change'], (event) => {
          if (event.action === 'change' || event.action === 'published') {
            location.reload(true)
          }
        })
      }
    }
  }, [])

  const getStoryblokData = async () => {
    try {
      const allRequests = getAllStoryblokRequests(apiRequests, sbSlug, sbLocale)

      if (allRequests) {
        const response = await Promise.allSettled(allRequests)

        if (response) {
          const fetchedStories = response.reduce((acc, value, i) => {
            if (value.status === 'rejected') {
              const key = apiRequests[`${sbSlug}`].endpoints[i].key
              const fallback =
                fallbackData &&
                fallbackData[locale] &&
                fallbackData[locale][`${sbSlug}`] &&
                fallbackData[locale][`${sbSlug}`][key]

              return {
                ...acc,
                [key]: fallback || {},
              }
            }

            if (value.status === 'fulfilled' && value.value) {
              return {
                ...acc,
                [apiRequests[`${sbSlug}`].endpoints[i].key]: value.value.data,
              }
            }
          }, {})

          setStory(fetchedStories)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const loadSbPageInfo =
    apiRequests && apiRequests[sbSlug] && apiRequests[sbSlug].loadSbPageInfo

  const state = {
    ...story,
    hasSbPageInfo: loadSbPageInfo || false,
    cvInfo,
  }

  return (
    <StoryblokContext.Provider value={state}>
      {children}
    </StoryblokContext.Provider>
  )
}

export {
  StoryblokContext,
  StoryblokConsumer,
  StoryblokProvider,
  useStoryblokContext,
}
